import { useEffect, useCallback, useRef, useState } from 'react';
import type { ListingPageResult, ListingMeta, ListingDesignItem } from '../../domain/models/ProductListing';
import {
  readPlpRestore,
  savePlpRestore,
  clearPlpRestore,
} from '../../utils/plpScrollRestore';
import {
  savePlpSessionSnapshot,
  clearPlpSessionSnapshot,
  tryReadMobileListingHydration,
} from '../../utils/plpSessionSnapshot';

export interface UsePlpListingOptions {
  /** Fetch a single page (1-based). Return shape must match ListingPageResult */
  fetchPage: (pageNumber: number, opts?: { signal?: AbortSignal }) => Promise<ListingPageResult>;
  /** Current pathname – used for scroll restore key (or restoreKey when provided) */
  pathname: string;
  /** SSR/first-load: first page data so we don't refetch page 1 on client */
  initialData?: ListingPageResult | null;
  /** When the listing identity changes (e.g. search query), refetch. Ensures search PLP loads after navigate. */
  listingKey?: string;
  /** Optional restore key (e.g. pathname + search for search). When not set, pathname is used. */
  restoreKey?: string;
  /**
   * When true, back-from-PDP uses session listing snapshot (no refetch) + virtualizer scroll.
   * Desktop keeps rAF scroll restore without full snapshot.
   */
  isMobile?: boolean;
  /**
   * When true, skip infinite scroll fetch / abort in-flight load-more (e.g. global footer visible).
   * Inject from the listing page — PLP core does not read layout Redux.
   */
  shouldPauseInfiniteScroll?: () => boolean;
  /** Customer code to reload PLP when user log-in context resolves */
  customerCode?: string;
}

export interface UsePlpListingReturn {
  listingMeta: ListingMeta | null;
  allDesigns: ListingDesignItem[];
  currentPage: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  isError: boolean;
  /** When browse API returns redirect_url (e.g. with is_filtered), replace current URL with this */
  redirectUrl: string | null;
  loadMore: () => Promise<void>;
  /** Pass tapped product designId so back-restore can anchor the virtualized grid (mobile). */
  onBeforeNavigate: (clickedDesignId?: number) => void;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
  /** Mobile: anchor restore to this product row in PlpGrid (preferred over pixel scroll). */
  mobileRestoreDesignId: number | null;
  /** Mobile: saved window scroll Y (pixel restore / fallback when design row is missing). */
  mobileScrollRestoreOffset: number | null;
  /** Mobile: after PlpGrid applies scroll offset; clears session restore + snapshot. */
  onMobileScrollRestoreComplete: () => void;
  /** Avoid PlpBase shimmer smooth scroll-to-top while session restore is active. */
  suppressShimmerScrollToTop: boolean;
  /** Echo of optional pause predicate for PlpGrid (same ref behavior as loadMore). */
  shouldPauseInfiniteScroll?: () => boolean;
}

function clearPlpSessionRestore(restorePath: string): void {
  clearPlpRestore(restorePath);
  clearPlpSessionSnapshot(restorePath);
}

export function usePlpListing({
  fetchPage,
  pathname,
  initialData,
  listingKey,
  restoreKey,
  isMobile = false,
  shouldPauseInfiniteScroll,
  customerCode,
}: UsePlpListingOptions): UsePlpListingReturn {
  const restorePath = restoreKey ?? pathname;
  /** Single identity for cache/restore; defaults to pathname when listingKey is omitted. */
  const identityKey = listingKey ?? pathname;

  // PDP back on mobile: prefer session snapshot (full loaded pages) over SSR first page only.
  const mobileHydration = isMobile ? tryReadMobileListingHydration(restorePath) : null;

  const [listingMeta, setListingMeta] = useState<ListingMeta | null>(() => {
    if (mobileHydration) return mobileHydration.snapshot.listingMeta;
    if (initialData) {
      return {
        header: initialData.header,
        headerImageUrl: initialData.headerImageUrl,
        totalCount: initialData.totalCount,
        filters: initialData.filters,
        filtersResetUrl: initialData.filtersResetUrl,
        exploreLinks: initialData.exploreLinks,
        listingSeo: initialData.listingSeo,
        faqs: initialData.faqs,
      };
    }
    return null;
  });

  const [allDesigns, setAllDesigns] = useState<ListingDesignItem[]>(() =>
    mobileHydration ? mobileHydration.snapshot.designs : initialData?.designs ?? []
  );

  const [currentPage, setCurrentPage] = useState(() =>
    mobileHydration ? mobileHydration.snapshot.currentPage : initialData ? 1 : 0
  );

  const [hasMore, setHasMore] = useState(
    () =>
      mobileHydration
        ? mobileHydration.snapshot.hasMore
        : initialData
          ? Boolean(initialData.totalCount > (initialData.designs?.length ?? 0))
          : false
  );

  const [isLoading, setIsLoading] = useState(
    () => !mobileHydration && !initialData
  );

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isError, setIsError] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(() => initialData?.redirectUrl ?? null);

  /**
   * Last listing identity that `allDesigns` / `listingMeta` correspond to.
   * When `identityKey` changes (e.g. filter URL), this lags until the new fetch completes so we
   * can treat the UI as loading immediately and avoid one paint of stale products.
   */
  const [resolvedListingKey, setResolvedListingKey] = useState(identityKey);

  const isLoadingRef = useRef(!initialData && !mobileHydration);
  isLoadingRef.current = isLoading;

  const [restore, setRestore] = useState<{
    scrollTop: number;
    pageCount: number;
    restoreDesignId?: number;
  } | null>(() => {
    if (mobileHydration) {
      return {
        scrollTop: mobileHydration.restore.scrollTop,
        pageCount: mobileHydration.restore.pageCount,
        ...(mobileHydration.restore.restoreDesignId != null
          ? { restoreDesignId: mobileHydration.restore.restoreDesignId }
          : {}),
      };
    }
    // Prefer saved back-from-PDP restore over skipping when SSR/Redux initialData exists,
    // so mobile scroll restore + overlay run after navigate back (same path as browse PLP).
    const saved = readPlpRestore(restorePath);
    if (saved?.scrollTop != null && saved?.pageCount != null && saved.pageCount >= 1) {
      return {
        scrollTop: saved.scrollTop,
        pageCount: saved.pageCount,
        ...(saved.restoreDesignId != null ? { restoreDesignId: saved.restoreDesignId } : {}),
      };
    }
    if (initialData) return null;
    return null;
  });

  const initialDataUsed = useRef(Boolean(initialData) || Boolean(mobileHydration));
  const initialListingKeyRef = useRef(identityKey);
  const initialCustomerCodeRef = useRef(customerCode);
  const skipNetworkForSnapshotRef = useRef(Boolean(mobileHydration));

  const sentinelRef = useRef<HTMLDivElement>(null);
  const restorePageCount = restore?.pageCount ?? 0;

  const shouldPauseInfiniteScrollRef = useRef(shouldPauseInfiniteScroll);
  shouldPauseInfiniteScrollRef.current = shouldPauseInfiniteScroll;

  const loadMoreAbortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    if (!shouldPauseInfiniteScroll) return;
    if (shouldPauseInfiniteScroll()) {
      loadMoreAbortRef.current?.abort();
    }
  }, [shouldPauseInfiniteScroll]);

  useEffect(() => {
    if (identityKey !== initialListingKeyRef.current || customerCode !== initialCustomerCodeRef.current) {
      initialDataUsed.current = false;
      skipNetworkForSnapshotRef.current = false;
      setRedirectUrl(null);
    }
    // Always advance the "previous key" ref so returning to the initial URL
    // (e.g. clicking Reset after navigating to a filtered URL) also triggers a refetch.
    initialListingKeyRef.current = identityKey;
    initialCustomerCodeRef.current = customerCode;
  }, [identityKey, customerCode]);

  useEffect(() => {
    if (skipNetworkForSnapshotRef.current) {
      skipNetworkForSnapshotRef.current = false;
      isLoadingRef.current = false;
      initialDataUsed.current = true;
      setResolvedListingKey(identityKey);
      return;
    }

    if (initialDataUsed.current && !restore) return;
    const targetPages = restore ? restore.pageCount : 1;

    let isCancelled = false;
    const load = async () => {
      isLoadingRef.current = true;
      setIsLoading(true);
      setIsError(false);
      try {
        const first = await fetchPage(1);
        if (isCancelled) return;
        if (first.redirectUrl) setRedirectUrl(first.redirectUrl);
        setListingMeta({
          header: first.header,
          headerImageUrl: first.headerImageUrl,
          totalCount: first.totalCount,
          filters: first.filters,
          filtersResetUrl: first.filtersResetUrl,
          exploreLinks: first.exploreLinks,
          listingSeo: first.listingSeo,
          faqs: first.faqs,
        });
        setAllDesigns(first.designs);
        setCurrentPage(1);
        setHasMore(first.totalCount > first.designs.length);

        if (targetPages <= 1) {
          if (!isCancelled) {
            initialDataUsed.current = true;
            setResolvedListingKey(identityKey);
          }
          return;
        }

        const rest = await Promise.all(
          Array.from({ length: targetPages - 1 }, (_, i) => fetchPage(i + 2))
        );
        if (isCancelled) return;
        const merged = [first.designs, ...rest.map((r) => r.designs)].flat();
        setAllDesigns(merged);
        setCurrentPage(targetPages);
        setHasMore(first.totalCount > merged.length);
        if (!isCancelled) {
          initialDataUsed.current = true;
          setResolvedListingKey(identityKey);
        }
      } catch (error) {
        console.error('Failed to load listing', error);
        if (!isCancelled) {
          setIsError(true);
          setResolvedListingKey(identityKey);
        }
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    };

    void load();
    return () => {
      isCancelled = true;
    };
  }, [fetchPage, restorePageCount, restore, identityKey]);

  useEffect(() => {
    if (isMobile) return;
    if (isLoadingRef.current || !restore || !listingMeta || allDesigns.length === 0) return;
    const scrollTop = restore.scrollTop;
    clearPlpSessionRestore(restorePath);
    setRestore(null);
    const timer = requestAnimationFrame(() => {
      window.scrollTo({ top: scrollTop, behavior: 'auto' });
    });
    return () => cancelAnimationFrame(timer);
  }, [isLoading, restore, listingMeta, allDesigns.length, restorePath, isMobile]);

  const onMobileScrollRestoreComplete = useCallback(() => {
    clearPlpSessionRestore(restorePath);
    setRestore(null);
  }, [restorePath]);

  const loadMore = useCallback(async () => {
    if (!hasMore || isLoadingMore || !listingMeta) return;
    if (shouldPauseInfiniteScrollRef.current?.() ?? false) return;
    if (resolvedListingKey !== identityKey) return;

    if (typeof navigator !== 'undefined' && navigator && navigator.onLine === false) {
      return;
    }

    const ac = new AbortController();
    loadMoreAbortRef.current = ac;
    setIsLoadingMore(true);
    try {
      const next = await fetchPage(currentPage + 1, { signal: ac.signal });
      if (shouldPauseInfiniteScrollRef.current?.() ?? false) return;
      setAllDesigns((prev) => [...prev, ...next.designs]);
      setCurrentPage((p) => p + 1);
      setHasMore(
        listingMeta.totalCount > allDesigns.length + next.designs.length
      );
    } catch (error) {
      const aborted =
        ac.signal.aborted ||
        (error instanceof DOMException && error.name === 'AbortError') ||
        (typeof error === 'object' &&
          error !== null &&
          'name' in error &&
          (error as { name: string }).name === 'AbortError');
      if (!aborted) {
        console.error('Failed to load more', error);
      }
    } finally {
      if (loadMoreAbortRef.current === ac) {
        loadMoreAbortRef.current = null;
      }
      setIsLoadingMore(false);
    }
  }, [hasMore, isLoadingMore, listingMeta, currentPage, allDesigns.length, fetchPage, resolvedListingKey, identityKey]);

  const onBeforeNavigate = useCallback(
    (clickedDesignId?: number) => {
      const scrollTop = typeof window !== 'undefined' ? window.scrollY : 0;
      savePlpRestore(restorePath, {
        scrollTop,
        pageCount: currentPage,
        ...(clickedDesignId != null ? { restoreDesignId: clickedDesignId } : {}),
      });
      if (isMobile && listingMeta) {
        savePlpSessionSnapshot(restorePath, {
          version: 1,
          listingMeta,
          designs: allDesigns,
          currentPage,
          hasMore,
          pageCount: currentPage,
          scrollTop,
          ...(clickedDesignId != null ? { restoreDesignId: clickedDesignId } : {}),
        });
      }
    },
    [restorePath, currentPage, isMobile, listingMeta, allDesigns, hasMore]
  );

  const mobileRestoreDesignId =
    isMobile && restore?.restoreDesignId != null ? restore.restoreDesignId : null;
  const mobileScrollRestoreOffset = isMobile && restore ? restore.scrollTop : null;
  const suppressShimmerScrollToTop = Boolean(restore);

  // While the URL identity has changed but the new fetch hasn't resolved yet, treat
  // the UI as loading so consumers (PlpBase) hide the grid instead of painting
  // stale rows for one frame.
  const effectiveIsLoading = isLoading || resolvedListingKey !== identityKey;

  return {
    listingMeta,
    allDesigns,
    currentPage,
    hasMore,
    isLoading: effectiveIsLoading,
    isLoadingMore,
    isError,
    redirectUrl,
    loadMore,
    onBeforeNavigate,
    sentinelRef,
    mobileRestoreDesignId,
    mobileScrollRestoreOffset,
    onMobileScrollRestoreComplete,
    suppressShimmerScrollToTop,
    shouldPauseInfiniteScroll,
  };
}
